"use client";

import React, { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { useTheme } from "next-themes";
import { useRouter, useSearchParams } from "next/navigation";
import { type BlogPost } from "@/lib/data/blogs";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import BlogFooter from "@/components/blog/BlogFooter";
import EnhancedBlogCard from "@/components/blog/EnhancedBlogCard";
import EnhancedBlogListHeader from "@/components/blog/EnhancedBlogListHeader";
import BlogHeader from "@/components/blog/BlogHeader";
import BlogSidebar from "@/components/blog/BlogSidebar";
import FollowMe from "@/components/blog/FollowMe";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCTAByKey } from "@/lib/actions/cta";
import { subscribeEmail } from "@/lib/actions/subscribe";
import type { CTASettings } from "@/lib/actions/cta";
import { getInternalBlogLink } from "@/lib/utils/blog-url";

interface BlogsPageClientProps {
  blogsData?: any[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalPosts: number;
  };
  serverFilters: {
    search: string;
    category: string;
    tags: string[];
  };
  allCategories: string[];
  allTags: string[];
}

// Pagination Component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) => {
  const maxPageButtons = 5;
  const startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
  const endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
  const adjustedStartPage = Math.max(1, endPage - maxPageButtons + 1);

  const pageNumbers = Array.from(
    { length: endPage - adjustedStartPage + 1 },
    (_, i) => adjustedStartPage + i
  );

  return (
    <div className="flex justify-center items-center gap-2 mt-24">
      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="p-4 rounded-xl border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        aria-label="Previous page"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {adjustedStartPage > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            className="w-12 h-12 rounded-xl border border-border hover:bg-primary hover:text-primary-foreground transition-all text-sm font-bold"
          >
            1
          </button>
          {adjustedStartPage > 2 && (
            <span className="px-2 text-muted-foreground/30 font-bold">...</span>
          )}
        </>
      )}

      {pageNumbers.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={cn(
            "w-12 h-12 rounded-xl text-sm font-bold transition-all border",
            currentPage === page
              ? "bg-foreground text-background border-foreground shadow-sm"
              : "border-border hover:bg-muted text-foreground"
          )}
        >
          {page}
        </button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && (
            <span className="px-2 text-muted-foreground/30 font-bold">...</span>
          )}
          <button
            onClick={() => onPageChange(totalPages)}
            className="w-12 h-12 rounded-xl border border-border hover:bg-primary hover:text-primary-foreground transition-all text-sm font-bold"
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="p-4 rounded-xl border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        aria-label="Next page"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

// Main Component
export default function BlogsPageClient({
  blogsData,
  pagination,
  serverFilters,
  allCategories: initialCategories,
  allTags: initialTags,
}: BlogsPageClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(serverFilters?.search || "");

  // Sync state with props
  useEffect(() => {
    setSearchQuery(serverFilters?.search || "");
  }, [serverFilters?.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== (serverFilters?.search || "")) {
        updateFilters({ search: searchQuery, page: 1 });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const posts = blogsData || [];

  // Featured post logic: Pick first post of page 1 if no filters
  const isFirstPage = pagination?.currentPage === 1;
  const hasActiveFilters = Boolean(
    serverFilters?.search ||
      (serverFilters?.category && serverFilters?.category !== "All") ||
      serverFilters?.tags?.length > 0
  );

  const featuredPost =
    !hasActiveFilters && isFirstPage && posts.length > 0 ? posts[0] : null;
  const gridPosts = featuredPost ? posts.slice(1) : posts;

  const updateFilters = (updates: any) => {
    // If category is selected, navigate to category page
    if (updates.category !== undefined) {
      if (updates.category && updates.category !== "All") {
        router.push(
          getInternalBlogLink(`/category/${updates.category.toLowerCase()}`)
        );
        return;
      }
    }

    // If tag is selected, navigate to tag page
    if (updates.tag !== undefined) {
      if (updates.tag) {
        router.push(
          getInternalBlogLink(`/tag/${encodeURIComponent(updates.tag)}`)
        );
        return;
      }
    }

    // For search and pagination, use query params on main blog page
    const params = new URLSearchParams(searchParams.toString());

    if (updates.page !== undefined) {
      if (updates.page > 1) params.set("page", updates.page.toString());
      else params.delete("page");
    }

    if (updates.search !== undefined) {
      if (updates.search) params.set("search", updates.search);
      else params.delete("search");
    }

    const queryString = params.toString();
    router.push(getInternalBlogLink(queryString ? `/?${queryString}` : "/"));
  };

  const clearFilters = () => {
    setSearchQuery("");
    router.push(getInternalBlogLink("/"));
  };

  return (
    <>
      <BlogHeader />
      <main className="min-h-screen relative">
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-12 md:pb-20">
          <EnhancedBlogListHeader
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCategory={serverFilters?.category || "All"}
            onCategoryChange={(category) =>
              updateFilters({ category, page: 1 })
            }
            categories={["All", ...initialCategories]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8 md:mt-16">
            {/* Main Content Area */}
            <div className="lg:col-span-8 space-y-12">
              {posts.length > 0 ? (
                <>
                  {featuredPost && (
                    <EnhancedBlogCard
                      post={featuredPost}
                      index={0}
                      variant="featured"
                    />
                  )}

                  {gridPosts.length > 0 && (
                    <div className="divide-y divide-border/10">
                      <AnimatePresence mode="popLayout">
                        {gridPosts.map((post: BlogPost, index: number) => (
                          <EnhancedBlogCard
                            key={post.slug}
                            post={post}
                            index={index + (featuredPost ? 1 : 0)}
                            variant="default"
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  )}

                  {pagination?.totalPages > 1 && (
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      onPageChange={(page) => updateFilters({ page })}
                    />
                  )}
                </>
              ) : (
                <div className="py-20 text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 mb-4">
                    <Search className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold">No articles found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search or filters.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:scale-105 transition-all"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar Area */}
            <div className="lg:col-span-4 hidden lg:block border-l border-border/10 pl-8">
              <BlogSidebar
                popularPosts={posts.slice(0, 5)} // Simulating popular posts with top of list
                categories={initialCategories}
                selectedCategory={serverFilters?.category || null}
                onCategoryChange={(category) =>
                  updateFilters({ category, page: 1 })
                }
              />
            </div>
          </div>

        </div>

        <BlogFooter />
      </main>
    </>
  );
}

